/**
 * Hosted-meeting and board join codes out of analytics (P7 design 8.3).
 *
 * `/m/<code>` admits people to a meeting (and, for P4, to a board), so the
 * code is a credential in a URL. PostHog copies URLs into `$current_url`,
 * `$pathname`, `$referrer`, `$initial_*` and more, so `before_send` runs every
 * string property through this: the path keeps its shape and loses the code,
 * `/m/[REDACTED]`. It covers the plain path, the hash route (`#/m/…`), the
 * board invite (`/m/…/board`) and the percent-encoded forms.
 *
 * P3's meeting page `/flow/m/<flowPlanId>` is an id, not a code: left alone.
 */

const JOIN_CODE_PATH = /(?<!\/flow)(\/m\/)[0-9A-Za-z][0-9A-Za-z-]{3,31}(?=$|[/?#&\s"'%])/g;
const ENCODED_JOIN_CODE_PATH = /(?<!%2Fflow)(%2Fm%2F)[0-9A-Za-z][0-9A-Za-z-]{3,31}(?=$|%|[&\s"'])/gi;

export const JOIN_CODE_REDACTED = '[REDACTED]';

/** Replace every join code in one string. */
export function redactJoinCodeString(value: string): string {
  if (!value.includes('/m/') && !/%2Fm%2F/i.test(value)) return value;
  return value
    .replace(JOIN_CODE_PATH, `$1${JOIN_CODE_REDACTED}`)
    .replace(ENCODED_JOIN_CODE_PATH, `$1${JOIN_CODE_REDACTED}`);
}

/**
 * Recursively sanitise PostHog event properties. Depth is capped and cycles
 * are skipped, as in redact-signage-token.ts.
 */
export function redactJoinCodes<T>(value: T, depth = 0, seen = new WeakSet<object>()): T {
  if (typeof value === 'string') return redactJoinCodeString(value) as T;
  if (value == null || typeof value !== 'object' || depth >= 32) return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    return value.map((item) => redactJoinCodes(item, depth + 1, seen)) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    out[key] = redactJoinCodes(item, depth + 1, seen);
  }
  return out as T;
}
